import {
  type BulkPublisherActionBody,
  type BulkPublisherActionResponse,
  bulkPublisherActionResponseDto,
  type ProvisionPublisherBody,
  type ProvisionPublisherResponse,
  type PublisherActivityPage,
  type PublisherDetail,
  type PublisherStatus,
  type PublisherWithStats,
  provisionPublisherResponseDto,
  publisherActivityPageDto,
  publisherDetailResponseDto,
  publishersWithStatsListResponseDto,
  type SetPublisherStatusBody,
  type VerifyPublisherResponse,
  verifyPublisherResponseDto,
} from '@planuze/admin-contracts';
import { ApiError, parseJson, readErrorCode, request } from './http.js';

const HTTP_FORBIDDEN = 403;
const HTTP_BAD_REQUEST = 400;
const HTTP_CONFLICT = 409;

/* Cliente REST de governança de publishers (ADR-0405 + ADR-0471). Consome o admin-service
   GET /v1/admin/publishers (list + stats agregado) + POST /:id/verify (root-sign) + POST
   /:id/status (suspend/ban + reversos) + POST /bulk (ação em lote). O admin lê/escreve o
   licensing via HMAC. */

async function publisherError(res: Response): Promise<ApiError> {
  const code = await readErrorCode(res);
  return new ApiError(res.status, 'server_error', code || 'request failed');
}

export async function listPublishers(status?: string): Promise<readonly PublisherWithStats[]> {
  const query =
    status !== undefined && status.length > 0 ? `?status=${encodeURIComponent(status)}` : '';
  const res = await request(`/v1/admin/publishers${query}`);
  if (!res.ok) throw await publisherError(res);
  const page = await parseJson(res, publishersWithStatsListResponseDto);
  return page.publishers;
}

/** GET /v1/admin/publishers/:id — detalhe (identidade + chaves + domainVerified + stats). */
export async function getPublisher(id: string): Promise<PublisherDetail> {
  const res = await request(`/v1/admin/publishers/${encodeURIComponent(id)}`);
  if (!res.ok) throw await publisherError(res);
  return (await parseJson(res, publisherDetailResponseDto)).publisher;
}

/** GET /v1/admin/publishers/:id/activity — página keyset da trilha de atividade (cursor opaco).
    Resolve "publisher sem histórico": tokens/chaves/verificação de domínio, mais recente primeiro. */
export async function getPublisherActivity(
  id: string,
  cursor?: string,
): Promise<PublisherActivityPage> {
  const query =
    cursor !== undefined && cursor.length > 0 ? `?cursor=${encodeURIComponent(cursor)}` : '';
  const res = await request(`/v1/admin/publishers/${encodeURIComponent(id)}/activity${query}`);
  if (!res.ok) throw await publisherError(res);
  return parseJson(res, publisherActivityPageDto);
}

export async function verifyPublisher(id: string): Promise<VerifyPublisherResponse> {
  const res = await request(`/v1/admin/publishers/${encodeURIComponent(id)}/verify`, {
    method: 'POST',
  });
  if (!res.ok) throw await publisherError(res);
  return parseJson(res, verifyPublisherResponseDto);
}

export async function setPublisherStatus(
  id: string,
  status: PublisherStatus,
  reason: string,
): Promise<void> {
  const body: SetPublisherStatusBody = { status, reason };
  const res = await request(`/v1/admin/publishers/${encodeURIComponent(id)}/status`, {
    method: 'POST',
    body,
  });
  if (!res.ok) throw await publisherError(res);
}

export async function bulkPublisherAction(
  body: BulkPublisherActionBody,
): Promise<BulkPublisherActionResponse> {
  const res = await request('/v1/admin/publishers/bulk', { method: 'POST', body });
  if (!res.ok) throw await publisherError(res);
  return parseJson(res, bulkPublisherActionResponseDto);
}

/* Erros do provisionamento (ADR-0483): 403 sem permissão (super_admin); 400 owner_not_found
   (owner deixou de existir entre o pick e o submit) / invalid_slug; 409 slug_taken /
   already_publisher (o owner já tem publisher) / conflict. Demais → server_error. */
async function provisionError(res: Response): Promise<ApiError> {
  const code = await readErrorCode(res);
  if (res.status === HTTP_FORBIDDEN) return new ApiError(res.status, 'unauthorized', 'forbidden');
  if (res.status === HTTP_BAD_REQUEST && (code === 'owner_not_found' || code === 'invalid_slug')) {
    return new ApiError(res.status, code, code);
  }
  if (
    res.status === HTTP_CONFLICT &&
    (code === 'slug_taken' || code === 'already_publisher' || code === 'conflict')
  ) {
    return new ApiError(res.status, code, code);
  }
  return new ApiError(res.status, 'server_error', code || 'provision failed');
}

/** POST /v1/admin/publishers/provision — provisiona publisher designando o owner (admin-created,
    permite slugs reservados). Retorna o publisher criado (p/ navegar ao detalhe). */
export async function provisionPublisher(
  body: ProvisionPublisherBody,
): Promise<ProvisionPublisherResponse['publisher']> {
  const res = await request('/v1/admin/publishers/provision', { method: 'POST', body });
  if (!res.ok) throw await provisionError(res);
  return (await parseJson(res, provisionPublisherResponseDto)).publisher;
}
